import { useEffect } from 'react';

import { resolveConnector, resolveGeometryShapeIds } from '../../Core/UmlConnectorResolver.js';
import { waypointStrategySelector } from '../../Core/ConnectorFix/WaypointStrategySelector.js';
import { flattenClassShapes } from '../../Core/Import/CanvasShapeFlattener.js';
import { runAutoLayoutSeqDia } from '../../Core/AutoLayout/AutoLayoutSeqDia.js';

import umlCanvasEngine from '../../Core/UmlCanvasEngine.js';
import umlConnectorEngine from '../../Core/UmlConnectorEngine.js';
import umlUndoEngine from '../../Core/UmlUndoEngine/UmlUndoEngine.js';
import umlUndoRestorer from '../../Core/UmlUndoEngine/UmlUndoRestorer.js';
import { saveCurrentSchema } from '../../Core/UmlCanvasSaveAction.js';
import { requestPreviewUpdate } from '../../Core/Preview/UmlPreviewTrigger.js';
import componentNotifier from '../../../services/wss/ComponentNotifier';
import { EngineNames } from '../../../services/wss/events/EngineNames';

import userSettingsEngine from '@/controllers/UserSettings/UserSettingsEngine';
import { OpenToast } from '@/JxControls/index';

/**
 *
 * @param {Array} connectors - real connectors (umlConnectorEngine.connectors)
 * @param {Array} visibleConnectors - resolveInterfaceReduction(...).visibleConnectors
 * @param {string|null} selectedId
 * @param {Function} getShapeById
 * @param {Array} shapes - umlCanvasEngine.shapes (top-level, incl. package children via flattenClassShapes) — used for C/E's "all classes" fallback
 */
export default function useConnectorKeyboardShortcuts(connectors, visibleConnectors, selectedId, getShapeById, shapes) {
    useEffect(
        function attachConnectorKeyboardShortcuts() {
            function onKeyDown(ev) {
                if (ev.target.tagName === 'INPUT' || ev.target.tagName === 'TEXTAREA') return;

                if (ev.key.toLowerCase() === 'r') {
                    const connector = resolveConnector(selectedId, connectors);
                    if (!connector) return;
                    umlConnectorEngine.setConnectorAnchor(connector.id, 'start', null);
                    umlConnectorEngine.setConnectorAnchor(connector.id, 'end', null);

                    ev.preventDefault();
                }

                // single line fix of attached connectors of shape?
                if (ev.key.toLowerCase() === 'g') {
                    const connector = resolveConnector(selectedId, connectors);
                    if (!connector) return;

                    const { fromShapeId, toShapeId } = resolveGeometryShapeIds(connector, visibleConnectors);
                    const fromShape = getShapeById(fromShapeId);
                    const toShape = getShapeById(toShapeId);

                    waypointStrategySelector(connector, fromShape, toShape);

                    umlCanvasEngine.notify();
                    ev.preventDefault();
                }

                // toggle hover log on/offhh
                if (ev.key.toLowerCase() === 'h') {
                    const hover = userSettingsEngine.resolve('umlWorkspace.logHoverMessages');
                    const switched = !hover ? 'on' : 'off';

                    userSettingsEngine.set('umlWorkspace.logHoverMessages', !hover);
                    OpenToast(`Hover log switched ${switched}`, 'info');

                    ev.preventDefault();
                }

                // A: auto-layout the sequence diagram — labels off activation
                // bars, cramped gaps widened, participants top-aligned (see
                // Core/AutoLayout/AutoLayoutSeqDia.js). No selection needed.
                if (ev.key.toLowerCase() === 'a') {
                    runAutoLayoutSeqDia();
                    ev.preventDefault();
                }

                // P: cycle CsSeqDia call-label parameters hidden -> types -> full
                // (types+names) -> hidden (see UmlCanvasEngine.cycleSeqParamsMode).
                // No selection needed, same as A. Toast feedback since there's no
                // toolbar indicator for which of the 3 states is active.
                if (ev.key.toLowerCase() === 'p') {
                    umlCanvasEngine.cycleSeqParamsMode();
                    OpenToast(`Parameters: ${umlCanvasEngine.viewConfig.seqParamsMode}`, 'info');
                    ev.preventDefault();
                }

                // save schema + update preview
                if (ev.key.toLowerCase() === 's') {
                    saveCurrentSchema().then(requestPreviewUpdate);
                    ev.preventDefault();
                }

                // delete is coded in
                // useShapeDeleteShortcut
                // and called in UmlCanvas.useShapeDeleteShortcut.js

                // fix lines
                if (ev.key.toLowerCase() === 'f') {
                    umlConnectorEngine.straightenAllConnectors('straightenExisting', getShapeById, visibleConnectors);
                    ev.preventDefault();
                }

                // expand or collapse umlClass
                if (ev.key.toLowerCase() === 'e') {
                    const selectedShape = getShapeById(selectedId);
                    if (selectedShape?.kind === 'class') {
                        umlCanvasEngine.setShapeCollapsed(selectedShape.id, !selectedShape.collapsed);
                    } else {
                        flattenClassShapes(shapes).forEach(function toggleEach(shape) {
                            umlCanvasEngine.setShapeCollapsed(shape.id, !shape.collapsed);
                        });
                    }
                    ev.preventDefault();
                }

                // X: hide the selected shape — adds its id to viewConfig.userHiddenItems
                if (ev.key.toLowerCase() === 'x') {
                    const selectedShape = getShapeById(selectedId);
                    if (!selectedShape) return;
                    umlCanvasEngine.setItemHidden(selectedShape.id, true);
                    ev.preventDefault();
                }

                // C: "Copy Package" — opens the Save-to-library dialog for the selected package.
                if (ev.key.toLowerCase() === 'c') {
                    const selectedShape = getShapeById(selectedId);
                    if (selectedShape?.kind !== 'package') return;
                    componentNotifier.notify(EngineNames.UmlPkgSaveDlg, 'Open', selectedShape);
                    ev.preventDefault();
                }

                // Ctrl/Cmd+Z: pop the last snapshot off UmlUndoEngine
                if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'z') {
                    const undoData = umlUndoEngine.undo();
                    umlUndoRestorer.restore(undoData);
                    ev.preventDefault();
                }
            }

            window.addEventListener('keydown', onKeyDown, true);
            return function detachConnectorKeyboardShortcuts() {
                window.removeEventListener('keydown', onKeyDown, true);
            };
        },
        [connectors, visibleConnectors, selectedId, getShapeById, shapes],
    );
}
